import type * as React from "react"
import { css } from "@digits-css"
import "./scrollbar.digits.css"

const DEFAULT_INSET = "12px"

/**
 * Per-UA base values for `--scrollbar-gutter-inset`, applied at `:root` so
 * every scroller inherits a sensible default. Consumers stack additional
 * spacing via `--scrollbar-gutter-offset` on the scroller (or any ancestor).
 *
 * Plain-string export so it can be interpolated into either @digits-css or
 * styled-components global stylesheets. Inject once at the app level.
 */
export const stableScrollbarGutterRootStyles = `
  :root {
    --scrollbar-gutter-inset: ${DEFAULT_INSET};
  }
  :root.firefox {
    --scrollbar-gutter-inset: 14px;
  }
  :root.safari {
    --scrollbar-gutter-inset: 18px;
  }
`

/**
 * Stable scrollbar gutter for both classic and overlay scrollbar modes.
 *
 * scrollbar-gutter: stable reserves gutter space for classic scrollbars so
 * content doesn't reflow when scrolling kicks in. The negative margin-right
 * pushes the element into the parent's padding, placing the gutter (and
 * scrollbar) there instead of eating into content width.
 *
 * Two custom properties tune the result:
 * - `--scrollbar-gutter-inset` is the base inset (how far past the parent's
 *   content edge the scrollbar lands). Defaults to per-UA values inherited
 *   from `:root` via stableScrollbarGutterRootStyles.
 * - `--scrollbar-gutter-offset` is an additional offset a consumer can stack
 *   on top of the base when they want the scrollbar pushed further out.
 *
 * Modeling the consumer hook as an additive offset means the per-UA default
 * still applies in every browser; the consumer doesn't have to re-derive
 * the base for each UA.
 *
 * Requires the parent to have enough right padding to fit the scrollbar.
 */
export const stableScrollbarGutter = css`
  scrollbar-gutter: stable;
  --effective-gutter-inset: calc(
    var(--scrollbar-gutter-inset, ${DEFAULT_INSET}) + var(--scrollbar-gutter-offset, 0px)
  );
  --overlay-inset: calc(
    var(--effective-gutter-inset) - min(var(--effective-gutter-inset), var(--scrollbar-width, 0px))
  );
  padding-right: var(--overlay-inset);
  margin-right: calc(-1 * var(--overlay-inset) - var(--scrollbar-width, 0px));
`

export const stableScrollbarGutterStyle: React.CSSProperties = {
  scrollbarGutter: "stable",
  ["--effective-gutter-inset" as string]: `calc(var(--scrollbar-gutter-inset, ${DEFAULT_INSET}) + var(--scrollbar-gutter-offset, 0px))`,
  ["--overlay-inset" as string]:
    "calc(var(--effective-gutter-inset) - min(var(--effective-gutter-inset), var(--scrollbar-width, 0px)))",
  paddingRight: "var(--overlay-inset)",
  marginRight: "calc(-1 * var(--overlay-inset) - var(--scrollbar-width, 0px))",
}
